import React, { useState, ComponentProps } from 'react';
import classnames from 'classnames';
import axios from 'axios';
import { triggerSignUp } from '@fiverr-private/identification_triggers';
import { Button } from '@fiverr-private/fit';
import pathfinder from '@fiverr-private/pathfinder';
import { getContext } from '@fiverr-private/fiverr_context';
import { logger, mixpanel } from '@fiverr-private/obs';
import withTranslation from '../../lib/withTranslation';
import { sendBigQueryEvent } from '../../utils/bigQuery';
import { sendMixPanelEvent } from '../../utils/mixPanel';
import { VARIANTS } from '../../utils/experiments';
import { BIGQUERY_EVENT, EXPERIMENT_STATE, ONBOARDING_EXPERIMENT_ID } from './constants';
import './style.scss';

interface JoinLinkProps {
    fill?: ComponentProps<typeof Button>['fill'];
    color?: ComponentProps<typeof Button>['color'];
    size?: ComponentProps<typeof Button>['size'];
    textColor?: ComponentProps<typeof Button>['textColor'];
    i18n: any;
    isThemed?: boolean;
    inLynxesSeamlessOnboardingAndRoutingRollout?: boolean;
    inLynxesSeamlessOnboardingMixpanelRecordingsRollout?: boolean;
    isEligibleForOnboardingExperience?: boolean;
}

const JoinLink = ({
    fill = (Button as any).FILLS.GHOST,
    color = (Button as any).TEXT_COLORS.GREEN,
    size = (Button as any).SIZES.SMALL,
    textColor,
    i18n,
    isThemed = false,
    inLynxesSeamlessOnboardingAndRoutingRollout = false,
    inLynxesSeamlessOnboardingMixpanelRecordingsRollout = false,
    isEligibleForOnboardingExperience = false,
}: JoinLinkProps) => {
    const [loading, setLoading] = useState(false);
    const context = getContext();

    const existingOnboardingGroup =
        typeof context.abTests[ONBOARDING_EXPERIMENT_ID] !== 'undefined'
            ? Number(context.abTests[ONBOARDING_EXPERIMENT_ID])
            : null;

    const checkOnboardingEligibility = () => {
        if (!isEligibleForOnboardingExperience) {
            return false;
        }

        if (existingOnboardingGroup) {
            return true;
        }

        if (!inLynxesSeamlessOnboardingAndRoutingRollout) {
            return false;
        }

        return true;
    };

    const triggerClassName = classnames('fiverr-header-join', { 'themed-link': isThemed });

    const path = pathfinder(
        'join',
        {},
        {
            query: {
                source: 'top_nav',
            },
        }
    );

    const onboardingPath = pathfinder(
        'signup_page',
        {},
        {
            query: {
                source: 'top_nav',
            },
        }
    );

    const allocateToOnboardingTest = async () => {
        try {
            setLoading(true);
            const { data } = await axios.post(pathfinder('experiment_allocate_api'), {
                experiment_id: ONBOARDING_EXPERIMENT_ID,
                type: 'guest',
                num_of_groups: 2,
            });
            setLoading(false);

            if (data?.state === EXPERIMENT_STATE.LOCKED || data?.state === EXPERIMENT_STATE.DISABLED) {
                logger.error('Failed to allocate to onboarding test', {
                    description: `Attempt to allocate to an experiment in state: ${data.state} failed. ExperimentId: ${ONBOARDING_EXPERIMENT_ID}.`,
                });
                return null;
            }

            return data.group as number;
        } catch (error) {
            logger.error('Failed to allocate to onboarding test', { error });
            setLoading(false);

            return null;
        }
    };

    const handleClick = async (event) => {
        const hasExistingOnboardingGroup = typeof existingOnboardingGroup === 'number';
        const shouldBeAllocatedToOnboardingTest = checkOnboardingEligibility() && !hasExistingOnboardingGroup;

        const clientAllocationGroup = shouldBeAllocatedToOnboardingTest ? await allocateToOnboardingTest() : null;

        const onboardingAllocationGroup = clientAllocationGroup || existingOnboardingGroup;
        const isAllocatedAndEligibleForOnboarding =
            typeof onboardingAllocationGroup === 'number' && checkOnboardingEligibility();

        if (isAllocatedAndEligibleForOnboarding && inLynxesSeamlessOnboardingMixpanelRecordingsRollout) {
            mixpanel.startSessionRecording();
        }

        const biEnrichmentParams = isAllocatedAndEligibleForOnboarding
            ? {
                  [`Experiment ${ONBOARDING_EXPERIMENT_ID}`]: onboardingAllocationGroup,
              }
            : undefined;

        sendMixPanelEvent({
            eventName: 'Header join button clicked',
            eventData: biEnrichmentParams || {},
        });
        sendBigQueryEvent(BIGQUERY_EVENT);

        if (isAllocatedAndEligibleForOnboarding && onboardingAllocationGroup === VARIANTS.B) {
            event.preventDefault();
            window.location.assign(onboardingPath);
            return;
        }

        try {
            event.preventDefault();
            setLoading(true);

            await triggerSignUp({
                modalOptions: { source: 'header_join_link' },
                biEnrichmentParams,
            });
        } catch {
            window.location.assign(path);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Button
            href={checkOnboardingEligibility() ? undefined : path}
            loading={loading}
            rel="nofollow"
            color={color}
            textColor={textColor}
            fill={fill}
            size={size}
            className={triggerClassName}
            onClick={handleClick}
        >
            {i18n.t('header.join')}
        </Button>
    );
};

export default withTranslation(JoinLink);
